import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import {
  CalendarDays, Plus, Pencil, Trash2, Loader2, ChevronLeft, ChevronRight,
  List, Grid as GridIcon, Mail, Users, Shield, Briefcase, X,
} from 'lucide-react';
import { LinkifiedText } from './LinkifiedText';
import AutopilotPanel from './AutopilotPanel';

/**
 * AdminCalendarTab
 * ---------------------------------------------------------------
 * Admin-only calendar that broadcasts events to every targeted agent.
 *
 * Audience routing:
 *   - general   → all agents
 *   - insurance → agents with "insurance" in their licenses
 *   - mortgage  → agents with "mortgage" in their licenses
 *
 * Edits/deletes here propagate instantly to every targeted agent's calendar
 * because each agent's GET /calendar merges admin events at read time.
 */
export default function AdminCalendarTab({ apiUrl, adminPassword }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [viewMode, setViewMode] = useState('month'); // 'month' | 'list'
  const [editing, setEditing] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [autoView, setAutoView] = useState(null); // read-only autopilot event (holiday / birthday)

  const monthKey = useMemo(() => {
    const y = currentMonth.getFullYear();
    const m = String(currentMonth.getMonth() + 1).padStart(2, '0');
    return `${y}-${m}`;
  }, [currentMonth]);

  const reload = useCallback(async () => {
    if (!adminPassword) return;
    setLoading(true);
    setError('');
    try {
      const url = viewMode === 'month'
        ? `${apiUrl}/api/admin/calendar?month=${monthKey}`
        : `${apiUrl}/api/admin/calendar`;
      const r = await fetch(url, { headers: { 'X-Admin-Password': adminPassword } });
      if (!r.ok) throw new Error(`Failed to load events (${r.status})`);
      const own = await r.json();
      const apUrl = viewMode === 'month'
        ? `${apiUrl}/api/admin/autopilot/calendar?month=${monthKey}`
        : `${apiUrl}/api/admin/autopilot/calendar`;
      const ar = await fetch(apUrl, { headers: { 'X-Admin-Password': adminPassword } });
      const auto = ar.ok ? await ar.json() : [];
      setEvents([...own, ...auto].sort((a, b) => (a.start_date || '').localeCompare(b.start_date || '')));
    } catch (e) {
      setError(e.message || 'Failed to load events');
    } finally {
      setLoading(false);
    }
  }, [apiUrl, adminPassword, monthKey, viewMode]);

  useEffect(() => { reload(); }, [reload]);

  const openCreate = (dateStr = '') => {
    setEditing(null);
    setShowModal({ start_date: dateStr });
  };
  const openEdit = (ev) => {
    if (ev.read_only || ev.source === 'autopilot') { setAutoView(ev); return; }
    setEditing(ev);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this event for ALL targeted agents? This cannot be undone.')) return;
    try {
      const r = await fetch(`${apiUrl}/api/admin/calendar/${id}`, {
        method: 'DELETE',
        headers: { 'X-Admin-Password': adminPassword },
      });
      if (!r.ok) throw new Error('Delete failed');
      setEvents(prev => prev.filter(e => e.id !== id));
    } catch (e) {
      alert(e.message || 'Delete failed');
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-calendar-tab">
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <CardTitle className="flex items-center gap-2">
              <CalendarDays className="h-5 w-5 text-indigo-500" />
              Company Calendar
              <span className="bg-indigo-50 text-indigo-700 text-xs font-semibold px-2 py-0.5 rounded-full ml-1">Admin</span>
            </CardTitle>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="inline-flex border rounded-md overflow-hidden" data-testid="view-toggle">
                <button
                  onClick={() => setViewMode('month')}
                  className={`px-3 py-1.5 text-sm flex items-center gap-1.5 ${viewMode === 'month' ? 'bg-indigo-500 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                  data-testid="view-month-btn"
                >
                  <GridIcon className="h-4 w-4" /> Month
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`px-3 py-1.5 text-sm flex items-center gap-1.5 border-l ${viewMode === 'list' ? 'bg-indigo-500 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                  data-testid="view-list-btn"
                >
                  <List className="h-4 w-4" /> List
                </button>
              </div>
              {viewMode === 'month' && (
                <div className="flex items-center gap-1">
                  <button onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))} className="p-1 hover:bg-gray-100 rounded" data-testid="cal-prev-month">
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  <span className="font-medium text-sm min-w-[140px] text-center">
                    {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                  </span>
                  <button onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))} className="p-1 hover:bg-gray-100 rounded" data-testid="cal-next-month">
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </div>
              )}
              <Button onClick={() => openCreate('')} className="bg-indigo-500 hover:bg-indigo-600" data-testid="add-event-btn">
                <Plus className="h-4 w-4 mr-1" /> Add Event
              </Button>
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-2 max-w-3xl">
            Events you create here automatically appear (read-only) on each
            targeted agent's personal calendar. Edits and deletions sync instantly.
          </p>
        </CardHeader>
        <CardContent>
          {error && <div className="bg-red-50 border-l-4 border-red-500 text-red-900 p-3 rounded mb-4 text-sm">{error}</div>}
          {loading ? (
            <div className="flex items-center justify-center py-12 text-gray-500">
              <Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading…
            </div>
          ) : viewMode === 'month' ? (
            <MonthGrid
              currentMonth={currentMonth}
              events={events}
              onDayClick={(dateStr) => openCreate(dateStr)}
              onEventClick={openEdit}
            />
          ) : (
            <ListView events={events} onEdit={openEdit} onDelete={handleDelete} />
          )}
        </CardContent>
      </Card>

      <AutopilotPanel apiUrl={apiUrl} adminPassword={adminPassword} />

      {autoView && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setAutoView(null)} data-testid="autopilot-event-view-modal">
          <Card className="w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
            <CardHeader className="flex flex-row items-start justify-between">
              <div>
                <CardTitle className="text-lg" data-testid="autopilot-event-view-title">{autoView.title}</CardTitle>
                <p className="text-xs text-gray-500 mt-1">{autoView.start_date} · All day · {autoView.autopilot?.kind === 'birthday' ? 'Team birthday' : `${autoView.autopilot?.category === 'major' ? 'Major' : 'Minor'} ${autoView.autopilot?.kind}`}{autoView.autopilot?.provinces ? ` · ${autoView.autopilot.provinces.join(', ')}` : autoView.autopilot?.kind !== 'birthday' ? ' · National' : ''}</p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setAutoView(null)} data-testid="autopilot-event-view-close"><X className="h-5 w-5" /></Button>
            </CardHeader>
            <CardContent className="space-y-3">
              {autoView.autopilot?.graphic_path && <img src={`${apiUrl}${autoView.autopilot.graphic_path}`} alt={autoView.title} className="w-full rounded-lg border" data-testid="autopilot-event-view-graphic" />}
              {autoView.description && <p className="text-sm text-gray-700">{autoView.description}</p>}
              <p className="text-xs text-gray-400">Placed automatically by the Calendar Autopilot — change its category, audience or switch it off in the Autopilot panel below.</p>
            </CardContent>
          </Card>
        </div>
      )}

      {showModal && (
        <EventModal
          apiUrl={apiUrl}
          adminPassword={adminPassword}
          editing={editing}
          initialDate={typeof showModal === 'object' ? showModal.start_date : ''}
          onClose={() => { setShowModal(false); setEditing(null); }}
          onSaved={() => { setShowModal(false); setEditing(null); reload(); }}
          onDelete={handleDelete}
        />
      )}
    </div>
  );
}

// ============================================================
// Month Grid
// ============================================================
function MonthGrid({ currentMonth, events, onDayClick, onEventClick }) {
  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const startingDay = new Date(year, month, 1).getDay();
  const today = new Date();
  const isThisMonth = today.getFullYear() === year && today.getMonth() === month;

  const eventsByDay = useMemo(() => {
    const map = {};
    for (const ev of events) {
      const d = ev.start_date;
      if (!d) continue;
      if (d.startsWith(`${year}-${String(month + 1).padStart(2, '0')}`)) {
        const day = Number(d.slice(8, 10));
        (map[day] = map[day] || []).push(ev);
      }
    }
    return map;
  }, [events, year, month]);

  const cells = [];
  for (let i = 0; i < startingDay; i++) cells.push(<div key={`e-${i}`} className="h-28 bg-gray-50 rounded" />);
  for (let d = 1; d <= daysInMonth; d++) {
    const dayEvents = eventsByDay[d] || [];
    const isToday = isThisMonth && today.getDate() === d;
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    cells.push(
      <div
        key={d}
        className={`h-28 border rounded p-1 overflow-hidden cursor-pointer hover:border-indigo-300 ${isToday ? 'bg-indigo-50 border-indigo-300' : 'bg-white border-gray-200'}`}
        onClick={() => onDayClick(dateStr)}
        data-testid={`cal-day-${dateStr}`}
      >
        <div className={`text-sm font-medium ${isToday ? 'text-indigo-700' : 'text-gray-700'}`}>{d}</div>
        <div className="space-y-0.5 mt-1">
          {dayEvents.slice(0, 3).map(ev => (
            <button
              key={ev.id}
              type="button"
              onClick={(e) => { e.stopPropagation(); onEventClick(ev); }}
              className="w-full text-left text-xs px-1 py-0.5 rounded truncate text-white"
              style={{ backgroundColor: ev.color || '#0077B6' }}
              title={`${ev.title} • ${ev.audience}`}
              data-testid={`cal-event-${ev.id}`}
            >
              {ev.title}
            </button>
          ))}
          {dayEvents.length > 3 && <div className="text-xs text-gray-500 px-1">+{dayEvents.length - 3} more</div>}
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-7 gap-1" data-testid="cal-month-grid">
      {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
        <div key={d} className="text-center text-xs font-medium text-gray-500 py-2">{d}</div>
      ))}
      {cells}
    </div>
  );
}

// ============================================================
// List View — events grouped by month
// ============================================================
function ListView({ events, onEdit, onDelete }) {
  if (!events.length) {
    return <p className="text-sm text-gray-500 text-center py-12">No company events yet. Click <b>Add Event</b> above to create one.</p>;
  }
  const grouped = {};
  for (const ev of events) {
    const k = (ev.start_date || '').slice(0, 7) || 'unknown';
    (grouped[k] = grouped[k] || []).push(ev);
  }
  const months = Object.keys(grouped).sort();
  return (
    <div className="space-y-6" data-testid="cal-list-view">
      {months.map(m => (
        <div key={m}>
          <h3 className="text-sm font-semibold text-gray-600 mb-2 border-b pb-1">
            {new Date(`${m}-01T00:00:00`).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
          </h3>
          <div className="space-y-2">
            {grouped[m].map(ev => <EventRow key={ev.id} ev={ev} onEdit={onEdit} onDelete={onDelete} />)}
          </div>
        </div>
      ))}
    </div>
  );
}

function AudienceBadge({ audience }) {
  const config = {
    general:   { label: 'All Agents',   icon: Users,     cls: 'bg-gray-100 text-gray-700' },
    insurance: { label: 'Insurance',    icon: Shield,    cls: 'bg-emerald-100 text-emerald-700' },
    mortgage:  { label: 'Mortgage',     icon: Briefcase, cls: 'bg-blue-100 text-blue-700' },
  }[audience] || { label: audience, icon: Users, cls: 'bg-gray-100 text-gray-700' };
  const Icon = config.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${config.cls}`}>
      <Icon className="h-3 w-3" /> {config.label}
    </span>
  );
}

function EventRow({ ev, onEdit, onDelete }) {
  const fmtDate = (d) => {
    if (!d) return '';
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(d);
    if (!m) return d;
    return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])).toLocaleDateString('en-CA', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  };
  return (
    <div className="border rounded-lg p-3 bg-white hover:shadow-sm transition flex items-center gap-3" data-testid={`cal-row-${ev.id}`}>
      <div className="w-2 h-12 rounded-full flex-shrink-0" style={{ backgroundColor: ev.color || '#0077B6' }} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <h4 className="font-semibold text-gray-800 truncate">{ev.title}</h4>
          <AudienceBadge audience={ev.audience} />
        </div>
        <p className="text-xs text-gray-500 mt-0.5">
          {fmtDate(ev.start_date)}
          {!ev.all_day && ev.start_time && <> · {ev.start_time}{ev.end_time ? `–${ev.end_time}` : ''}</>}
          {ev.all_day && <> · All day</>}
        </p>
        {ev.description && <p className="text-xs text-gray-600 mt-1"><LinkifiedText text={ev.description} /></p>}
      </div>
      <div className="flex gap-1 flex-shrink-0">
        {ev.source === 'autopilot' ? (
          <Button size="sm" variant="outline" onClick={() => onEdit(ev)} data-testid={`cal-view-${ev.id}`}>Autopilot</Button>
        ) : (
          <>
            <Button size="sm" variant="outline" onClick={() => onEdit(ev)} data-testid={`cal-edit-${ev.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
            <Button size="sm" variant="outline" className="text-red-600 border-red-300 hover:bg-red-50" onClick={() => onDelete(ev.id)} data-testid={`cal-delete-${ev.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
          </>
        )}
      </div>
    </div>
  );
}

// ============================================================
// Create / Edit Modal
// ============================================================
const COLORS = ['#0077B6', '#10b981', '#f59e0b', '#dc2626', '#7c3aed', '#0891b2', '#db2777'];

function EventModal({ apiUrl, adminPassword, editing, initialDate, onClose, onSaved, onDelete }) {
  const [form, setForm] = useState(() => ({
    title: editing?.title || '',
    description: editing?.description || '',
    start_date: editing?.start_date || initialDate || new Date().toISOString().slice(0, 10),
    start_time: editing?.start_time || '09:00',
    end_time: editing?.end_time || '10:00',
    all_day: editing?.all_day || false,
    color: editing?.color || '#0077B6',
    audience: editing?.audience || 'general',
    category: editing?.category || 'minor',
    notify_agents: editing ? false : true, // notify defaults to true on create, false on edit
  }));
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    if (!form.title.trim()) { setErr('Title is required'); return; }
    if (!form.start_date) { setErr('Date is required'); return; }
    setSubmitting(true);
    try {
      const url = editing
        ? `${apiUrl}/api/admin/calendar/${editing.id}`
        : `${apiUrl}/api/admin/calendar`;
      const method = editing ? 'PATCH' : 'POST';
      // PATCH doesn't accept notify_agents in the schema; only used on create
      const body = editing
        ? (() => { const { notify_agents, ...rest } = form; return rest; })()
        : form;
      const r = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', 'X-Admin-Password': adminPassword },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || `Save failed (${r.status})`);
      }
      onSaved();
    } catch (e2) {
      setErr(e2.message || 'Save failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="event-modal">
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-5 border-b flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <CalendarDays className="h-5 w-5 text-indigo-500" />
            {editing ? 'Edit Company Event' : 'New Company Event'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none" data-testid="close-event-modal">×</button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          {err && <div className="bg-red-50 border-l-4 border-red-500 text-red-900 p-3 rounded text-sm" data-testid="event-error">{err}</div>}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="w-full border rounded px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-300"
              required
              data-testid="event-title-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={2}
              className="w-full border rounded px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-300"
              data-testid="event-description-input"
              placeholder="Event details... (paste a link, e.g. https://zoom.us/j/123 — it becomes clickable)"
            />
            <p className="text-xs text-gray-400 mt-1">Tip: paste a full link (https://…) and it will be clickable for agents in-app and in email.</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Audience *</label>
            <div className="grid grid-cols-3 gap-2" data-testid="event-audience-group">
              {[
                { value: 'general',   label: 'All agents',   sub: 'Everyone',       icon: Users,     color: 'gray' },
                { value: 'insurance', label: 'Insurance & Investment', sub: 'Insurance / investment agents',   icon: Shield,    color: 'emerald' },
                { value: 'mortgage',  label: 'Mortgage',  sub: 'Mortgage agents only',    icon: Briefcase, color: 'blue' },
              ].map(opt => {
                const selected = form.audience === opt.value;
                const Icon = opt.icon;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setForm({ ...form, audience: opt.value })}
                    className={`border rounded-lg p-2.5 text-left transition ${selected ? 'border-indigo-500 bg-indigo-50 ring-2 ring-indigo-200' : 'border-gray-200 hover:border-gray-300 bg-white'}`}
                    data-testid={`event-audience-${opt.value}`}
                  >
                    <Icon className={`h-4 w-4 mb-1 ${selected ? 'text-indigo-600' : 'text-gray-500'}`} />
                    <div className={`text-sm font-semibold ${selected ? 'text-indigo-700' : 'text-gray-800'}`}>{opt.label}</div>
                    <div className="text-xs text-gray-500">{opt.sub}</div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
              <input
                type="date"
                value={form.start_date}
                onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                className="w-full border rounded px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-300"
                required
                data-testid="event-date-input"
              />
            </div>
            <div>
              <label className="flex items-center gap-2 mt-7 text-sm">
                <input
                  type="checkbox"
                  checked={form.all_day}
                  onChange={(e) => setForm({ ...form, all_day: e.target.checked })}
                  data-testid="event-allday-input"
                />
                All day
              </label>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Event importance</label>
            <div className="grid grid-cols-2 gap-2" data-testid="event-category-group">
              {[
                { value: 'major', label: 'Major event', sub: 'Autopilot creates a branded greeting graphic and emails it to the audience on the day' },
                { value: 'minor', label: 'Minor event', sub: 'Marked on calendars only' },
              ].map(opt => {
                const selected = form.category === opt.value;
                return (
                  <button key={opt.value} type="button" onClick={() => setForm({ ...form, category: opt.value })}
                    className={`border rounded-lg p-2.5 text-left transition ${selected ? 'border-indigo-500 bg-indigo-50 ring-2 ring-indigo-200' : 'border-gray-200 hover:border-gray-300 bg-white'}`}
                    data-testid={`event-category-${opt.value}`}>
                    <div className={`text-sm font-semibold ${selected ? 'text-indigo-700' : 'text-gray-800'}`}>{opt.label}</div>
                    <div className="text-xs text-gray-500">{opt.sub}</div>
                  </button>
                );
              })}
            </div>
          </div>

          {!form.all_day && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start time</label>
                <input
                  type="time"
                  value={form.start_time}
                  onChange={(e) => setForm({ ...form, start_time: e.target.value })}
                  className="w-full border rounded px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-300"
                  data-testid="event-start-time-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">End time</label>
                <input
                  type="time"
                  value={form.end_time}
                  onChange={(e) => setForm({ ...form, end_time: e.target.value })}
                  className="w-full border rounded px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-300"
                  data-testid="event-end-time-input"
                />
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Color</label>
            <div className="flex gap-2 flex-wrap" data-testid="event-color-group">
              {COLORS.map(c => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setForm({ ...form, color: c })}
                  className={`h-7 w-7 rounded-full border-2 ${form.color === c ? 'border-gray-800 scale-110' : 'border-white shadow'}`}
                  style={{ backgroundColor: c }}
                  data-testid={`event-color-${c.replace('#', '')}`}
                  aria-label={`Pick color ${c}`}
                />
              ))}
            </div>
          </div>

          {!editing && (
            <label className="flex items-start gap-2 text-sm bg-amber-50 border border-amber-200 rounded p-3" data-testid="event-notify-row">
              <input
                type="checkbox"
                checked={form.notify_agents}
                onChange={(e) => setForm({ ...form, notify_agents: e.target.checked })}
                className="mt-0.5"
                data-testid="event-notify-input"
              />
              <span className="flex-1">
                <span className="font-medium text-amber-900 flex items-center gap-1.5"><Mail className="h-4 w-4" /> Email all targeted agents about this event</span>
                <span className="text-xs text-amber-800 block mt-0.5">Uncheck to add the event silently (agents will still see it in their calendar).</span>
              </span>
            </label>
          )}

          <div className="flex gap-2 justify-between pt-3 border-t">
            <div>
              {editing && (
                <Button type="button" variant="outline" className="text-red-600 border-red-300 hover:bg-red-50"
                  onClick={() => { onClose(); onDelete(editing.id); }}
                  data-testid="event-delete-btn"
                >
                  <Trash2 className="h-4 w-4 mr-1" /> Delete
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={onClose} disabled={submitting}>Cancel</Button>
              <Button type="submit" className="bg-indigo-500 hover:bg-indigo-600" disabled={submitting} data-testid="event-save-btn">
                {submitting ? (<><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Saving…</>) : (editing ? 'Save Changes' : 'Create Event')}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
